<script setup>
import { ref } from 'vue';

const emit = defineEmits(['configure']);
const selectedExample = ref(0);
const selectedStep = ref(0);
const benefits = [
  {
    icon: 'i-hugeicons-ai-brain-01',
    title: 'Pedidos em linguagem natural',
    text: 'O assistente transforma sua solicitação em uma ferramenta disponível.',
  },
  {
    icon: 'i-hugeicons-time-02',
    title: 'Menos tarefas repetitivas',
    text: 'Um fluxo configurado pode reduzir cópias de dados entre etapas.',
  },
  {
    icon: 'i-hugeicons-bank',
    title: 'Conexão com os bancos',
    text: 'A operação usa as integrações habilitadas para sua conta.',
  },
];
const examples = [
  {
    title: 'Conhecer as ferramentas',
    icon: 'i-hugeicons-search-01',
    request:
      'Quais ferramentas bancárias estão disponíveis para a minha conta?',
    response:
      'Vou consultar o catálogo de ferramentas. Isso permite conhecer as operações disponíveis sem iniciar uma consulta bancária.',
    result: 'Primeiro contato com a integração',
    note: 'A listagem depende do catálogo e das permissões do usuário.',
  },
  {
    title: 'Preparar uma consulta',
    icon: 'i-hugeicons-user-check-01',
    request:
      'Quero consultar as opções para Letícia Gonçalves. O que preciso conferir antes?',
    response:
      'Primeiro, confira a autorização válida e os dados exigidos pelo banco na Talkvia. Com tudo confirmado, podemos usar a ferramenta disponível para a operação escolhida.',
    result: 'Orientação antes de executar',
    note: 'A autorização do cliente é necessária; a IA não pode aceitar em nome dele.',
  },
  {
    title: 'Entender uma pendência',
    icon: 'i-hugeicons-message-question',
    request:
      'A consulta de Tom Martins está aguardando autorização. Qual é o próximo passo?',
    response:
      'Conclua o fluxo de autorização indicado pelo banco. Depois, confirme o resultado antes de continuar. A pendência não significa que o crédito foi aprovado.',
    result: 'Próximo passo mais claro',
    note: 'Exemplo baseado em uma pendência informada, sem consulta real nesta página.',
  },
];
const steps = [
  {
    title: 'Prepare o acesso',
    icon: 'i-hugeicons-key-01',
    text: 'No Meu perfil, localize seu token pessoal. Confirme com o administrador a conta e os bancos habilitados.',
  },
  {
    title: 'Conecte o aplicativo',
    icon: 'i-hugeicons-plug-01',
    text: 'Escolha um aplicativo com MCP remoto por Streamable HTTP e cabeçalhos personalizados. Informe o servidor, o ambiente e seu token no campo de credenciais.',
  },
  {
    title: 'Confira as ferramentas',
    icon: 'i-hugeicons-checkmark-circle-02',
    text: 'Conecte e liste as ferramentas disponíveis. Esse primeiro teste não precisa executar uma operação bancária.',
  },
];
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex min-w-0 flex-col gap-5">
    <div class="grid gap-3 md:grid-cols-3">
      <article
        v-for="item in benefits"
        :key="item.title"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div
          class="mb-3 grid size-10 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
          aria-hidden="true"
        >
          <span :class="item.icon" class="size-6" />
        </div>
        <h3 class="text-sm font-semibold text-n-slate-12">{{ item.title }}</h3>
        <p class="mt-2 text-sm leading-relaxed text-n-slate-11">
          {{ item.text }}
        </p>
      </article>
    </div>
    <section
      class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1"
    >
      <header
        class="flex flex-wrap items-center justify-between gap-2 border-b border-n-weak p-4"
      >
        <div>
          <h3 class="font-semibold text-n-slate-12">Veja o MCP na prática</h3>
          <p class="mt-1 text-sm text-n-slate-11">
            Escolha um cenário para explorar.
          </p>
        </div>
        <span
          class="rounded-full bg-n-alpha-2 px-3 py-1 text-xs text-n-slate-11"
          >Exemplo ilustrativo · sem execução real</span
        >
      </header>
      <div class="grid md:grid-cols-[220px_minmax(0,1fr)]">
        <nav
          aria-label="Exemplos MCP"
          class="flex flex-col gap-2 border-b border-n-weak p-3 md:border-b-0 md:border-r"
        >
          <button
            v-for="(example, index) in examples"
            :key="example.title"
            type="button"
            :aria-pressed="selectedExample === index"
            :class="
              selectedExample === index
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            class="flex items-center gap-3 rounded-xl p-3 text-left text-sm font-medium"
            @click="selectedExample = index"
          >
            <span
              :class="example.icon"
              class="size-5 shrink-0"
              aria-hidden="true"
            />{{ example.title }}
          </button>
        </nav>
        <div
          class="flex min-w-0 flex-col gap-4 bg-n-alpha-1 p-4 sm:p-5"
          aria-live="polite"
          data-testid="mcp-example"
        >
          <div
            class="ml-6 rounded-2xl rounded-tr-sm border border-n-brand/20 bg-n-brand/5 p-4"
          >
            <span class="text-xs font-semibold text-n-brand">Você</span>
            <p class="mt-1 text-sm text-n-slate-12">
              {{ examples[selectedExample].request }}
            </p>
          </div>
          <div
            class="mr-6 rounded-2xl rounded-tl-sm border border-n-weak bg-n-solid-1 p-4"
          >
            <span class="text-xs font-semibold text-n-slate-11"
              >Assistente · resposta de exemplo</span
            >
            <p class="mt-1 text-sm leading-relaxed text-n-slate-12">
              {{ examples[selectedExample].response }}
            </p>
          </div>
          <div class="flex items-start gap-2 text-sm text-n-slate-11">
            <span
              class="i-hugeicons-checkmark-circle-02 mt-0.5 size-5 shrink-0 text-n-brand"
              aria-hidden="true"
            />
            <div>
              <strong class="font-medium text-n-slate-12">{{
                examples[selectedExample].result
              }}</strong>
              <p class="mt-1 text-xs">{{ examples[selectedExample].note }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="rounded-2xl border border-n-weak bg-n-solid-1 p-4 sm:p-5">
      <h3 class="font-semibold text-n-slate-12">Comece em 3 passos</h3>
      <div class="my-4 grid gap-2 sm:grid-cols-3">
        <button
          v-for="(step, index) in steps"
          :key="step.title"
          type="button"
          :aria-pressed="selectedStep === index"
          :class="
            selectedStep === index
              ? 'border-n-brand/40 bg-n-brand/5 text-n-brand'
              : 'border-n-weak text-n-slate-11'
          "
          class="flex items-center gap-2 rounded-xl border p-3 text-left text-sm"
          @click="selectedStep = index"
        >
          <span
            :class="step.icon"
            class="size-5 shrink-0"
            aria-hidden="true"
          /><span>{{ index + 1 }}. {{ step.title }}</span>
        </button>
      </div>
      <p class="text-sm leading-relaxed text-n-slate-11" aria-live="polite">
        {{ steps[selectedStep].text }}
      </p>
      <button
        type="button"
        class="mt-4 inline-flex items-center gap-2 rounded-lg bg-n-brand px-4 py-2 text-sm font-semibold text-white"
        data-testid="mcp-configure"
        @click="emit('configure')"
      >
        Ver configuração da conexão<span
          class="i-hugeicons-arrow-right-01 size-4"
          aria-hidden="true"
        />
      </button>
    </section>
    <p class="text-xs text-n-slate-11">
      Os resultados dependem da configuração, das permissões e dos bancos. O MCP
      não garante aprovação de crédito.
    </p>
  </div>
</template>
